import { useRef, useState } from "react";
import { Camera, X, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import AuthenticatedImage from "@/components/common/AuthenticatedImage";

const MAX_WIDTH = 1280;      // px — enough for evidence photos
const JPEG_QUALITY = 0.78;   // ~200-400KB per photo
const MAX_INPUT_MB = 25;     // reject if user uploads absurdly large file

/**
 * Compress an image file client-side (canvas → JPEG data-URI).
 * Also prevents the "screen stuck" issue when raw 10MB+ phone photos are
 * uploaded as base64 (which would balloon to ~14MB per photo).
 */
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_INPUT_MB * 1024 * 1024) {
      return reject(new Error(`Foto terlalu besar (max ${MAX_INPUT_MB}MB)`));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Gagal membaca file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("File bukan gambar yang valid"));
      img.onload = () => {
        try {
          const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
          resolve(out);
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Photo upload dropzone.
 * - Compresses image client-side.
 * - Uploads to object storage (Emergent) → gets a `photo:<uuid>` reference.
 * - Falls back to inline base64 if upload fails (keeps user unblocked offline).
 * - `value` in state can be either a `photo:<uuid>` ref OR a legacy `data:image/...` base64.
 */
export default function PhotoUpload({ label = "Bukti Foto", value, onChange, required, testid }) {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProcessing(true);
    try {
      const dataUri = await compressImage(file);
      // Try object-storage upload. If it fails, fall back to inline base64
      // so the user can still submit the form (data will migrate later).
      try {
        const { data } = await api.post("/photos", { data_uri: dataUri });
        if (data?.photo_id) {
          onChange(data.photo_id);
        } else {
          onChange(dataUri);
        }
      } catch (uploadErr) {
        console.warn("Photo storage upload failed, falling back to inline base64:", uploadErr);
        onChange(dataUri);
      }
    } catch (err) {
      toast.error(err.message || "Gagal memproses foto");
    } finally {
      setProcessing(false);
      // reset input so selecting the same file again re-triggers change
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {value && !processing && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[11px] text-red-600 hover:text-red-800 inline-flex items-center gap-1"
            data-testid={`${testid}-remove`}
          >
            <X size={12} /> Hapus
          </button>
        )}
      </div>
      {processing ? (
        <div className="relative w-full aspect-video rounded-md border-2 border-dashed border-sky-300 bg-sky-50 flex flex-col items-center justify-center gap-2 text-sky-700">
          <CircleNotch size={26} weight="bold" className="animate-spin" />
          <span className="text-xs font-medium">Mengompres & mengunggah foto…</span>
        </div>
      ) : value ? (
        <div className="relative w-full aspect-video rounded-md overflow-hidden border border-slate-200 bg-slate-50">
          <AuthenticatedImage src={value} alt={label} className="w-full h-full object-cover" />
        </div>
      ) : (
        <button
          type="button"
          data-testid={testid}
          onClick={() => inputRef.current?.click()}
          className="w-full aspect-video rounded-md border-2 border-dashed border-slate-300 bg-slate-50 hover:border-sky-500 hover:bg-sky-50 transition-colors duration-200 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-sky-700"
        >
          <Camera size={26} weight="duotone" />
          <span className="text-xs font-medium">Klik untuk unggah foto</span>
          <span className="text-[10px] text-slate-400">Akan dikompres otomatis · JPG, PNG, HEIC</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}
